import React, { Fragment, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getQuestionSubjects } from "../../../actions/adminQuestionActions";
import { getExams } from "../../../actions/adminExamActions";
import Loader from "../../Loader";

const QuestionBankSearch = () => {
  const dispatch = useDispatch();

  const {
    questionSubjects,
    loading,
    subjectPagination,
    questionHits = [],
    topicHits = [],
    sectionHits = [],
  } = useSelector((state) => state.questions);
  const { exams = [] } = useSelector((state) => state.exams);

  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [yearInput, setYearInput] = useState("");
  const [examId, setExamId] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState({
    search: "",
    year: "",
    examId: "",
  });

  const limit = 50;

  useEffect(() => {
    dispatch(getExams());
  }, [dispatch]);

  useEffect(() => {
    if (!hasSearched) return;
    dispatch(getQuestionSubjects(page, limit, appliedFilters));
  }, [dispatch, page, limit, appliedFilters, hasSearched]);

  const rows = useMemo(() => {
    if (!Array.isArray(questionSubjects)) return [];
    const flat = [];
    questionSubjects.forEach((examGroup) => {
      const subjects = Array.isArray(examGroup?.subjects)
        ? examGroup.subjects
        : [];
      subjects.forEach((subject) => {
        const years = Array.isArray(subject?.year) ? subject.year : [];
        if (years.length === 0) {
          flat.push({
            key: `${examGroup?.examId || "exam"}-${subject?._id}-none`,
            examName: examGroup?.examName || "N/A",
            subjectId: subject?._id,
            subjectTitle: subject?.title || "N/A",
            subjectSlug: subject?.slug || "",
            yearId: null,
            yearLabel: "—",
            totalQuestions: subject?.totalQuestions || 0,
            matchSource: subject?.matchSource || "",
          });
          return;
        }
        years.forEach((yearItem) => {
          flat.push({
            key: `${examGroup?.examId || "exam"}-${subject?._id}-${yearItem?._id}`,
            examName: examGroup?.examName || "N/A",
            subjectId: subject?._id,
            subjectTitle: subject?.title || "N/A",
            subjectSlug: subject?.slug || "",
            yearId: yearItem?._id,
            yearLabel: yearItem?.year || "—",
            totalQuestions:
              yearItem?.totalQuestions ?? subject?.totalQuestions ?? 0,
            matchSource: subject?.matchSource || "",
          });
        });
      });
    });
    return flat;
  }, [questionSubjects]);

  const handleSearch = (e) => {
    e.preventDefault();
    setHasSearched(true);
    setPage(1);
    setAppliedFilters({
      search: searchInput.trim(),
      year: yearInput.trim(),
      examId: examId || "",
    });
  };

  const handleClear = () => {
    setSearchInput("");
    setYearInput("");
    setExamId("");
    setPage(1);
    setHasSearched(false);
    setAppliedFilters({ search: "", year: "", examId: "" });
  };

  const openInNewTab = (path) => {
    if (!path) return;
    window.open(path, "_blank", "noopener,noreferrer");
  };

  const openSubjectYears = (row) => {
    if (row.subjectSlug) localStorage.setItem("subjectName", row.subjectSlug);
    if (row.examName) localStorage.setItem("examName", row.examName);
    openInNewTab(`/admin/subject-years/${row.subjectId}`);
  };

  const openYearTopics = (row) => {
    if (!row.yearId) {
      openSubjectYears(row);
      return;
    }
    if (row.subjectSlug) localStorage.setItem("subjectName", row.subjectSlug);
    if (row.yearLabel) localStorage.setItem("year", row.yearLabel);
    openInNewTab(`/admin/topicsection/${row.subjectId}/${row.yearId}`);
  };

  const openQuestionHit = (hit) => {
    if (hit.sectionId && hit.subjectId && hit.yearId && hit.topicId) {
      openInNewTab(
        `/admin/questions/${hit.sectionId}/${hit.subjectId}/${hit.yearId}/${hit.topicId}`
      );
      return;
    }
    if (hit.subjectId && hit.yearId) {
      openInNewTab(`/admin/topicsection/${hit.subjectId}/${hit.yearId}`);
      return;
    }
    if (hit.subjectId) {
      openInNewTab(`/admin/subject-years/${hit.subjectId}`);
    }
  };

  const openTopicHit = (hit) => {
    if (hit.subjectId) {
      openInNewTab(`/admin/subject-years/${hit.subjectId}`);
    }
  };

  return (
    <Fragment>
      <div className="content-wrapper">
        <section className="content-header">
          <h1>
            Question Bank
            <small>Global Search</small>
          </h1>
          <ol className="breadcrumb">
            <li>
              <Link to="/admin">
                <i className="fa fa-dashboard"></i> Dashboard
              </Link>
            </li>
            <li className="active">Question Bank Search</li>
          </ol>
        </section>

        <section className="content">
          <div className="row">
            <div className="col-xs-12">
              <div className="callout callout-info" style={{ marginBottom: "15px" }}>
                <h4 style={{ marginTop: 0 }}>What this page is for</h4>
                <p style={{ marginBottom: "8px" }}>
                  Use this to <strong>find and jump into</strong> the question
                  bank without clicking through Exam → Subject → Year → Topic.
                </p>
                <ul style={{ marginBottom: 0, paddingLeft: "18px" }}>
                  <li>
                    Search question wording (example: <em>Industry</em>) to open
                    the exact question list.
                  </li>
                  <li>
                    Search topic/section names to find where content is stored.
                  </li>
                  <li>
                    Filter by exam/year when you already know the location.
                  </li>
                </ul>
                <p style={{ marginTop: "8px", marginBottom: 0 }}>
                  The normal <Link to="/admin/question-subjects">Questions</Link>{" "}
                  menu is still for browsing and managing the full hierarchy.
                </p>
              </div>

              <div className="box box-primary">
                <div className="box-header with-border">
                  <h3 className="box-title">
                    <i className="fa fa-search"></i> Find questions faster
                  </h3>
                </div>
                <div className="box-body">
                  <form className="row" onSubmit={handleSearch}>
                    <div className="col-md-4" style={{ marginBottom: "10px" }}>
                      <label>Keyword</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Question text, topic, section, subject..."
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                      />
                      <small className="text-muted">
                        Best for question text / topic / section names
                      </small>
                    </div>
                    <div className="col-md-3" style={{ marginBottom: "10px" }}>
                      <label>Exam</label>
                      <select
                        className="form-control"
                        value={examId}
                        onChange={(e) => setExamId(e.target.value)}
                      >
                        <option value="">All exams</option>
                        {Array.isArray(exams) &&
                          exams.map((exam) => (
                            <option key={exam._id} value={exam._id}>
                              {exam.title}
                            </option>
                          ))}
                      </select>
                    </div>
                    <div className="col-md-2" style={{ marginBottom: "10px" }}>
                      <label>Year</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="e.g. 2019"
                        value={yearInput}
                        onChange={(e) => setYearInput(e.target.value)}
                      />
                    </div>
                    <div
                      className="col-md-3"
                      style={{ marginBottom: "10px", paddingTop: "25px" }}
                    >
                      <button type="submit" className="btn btn-primary">
                        <i className="fa fa-search"></i> Search
                      </button>{" "}
                      <button
                        type="button"
                        className="btn btn-default"
                        onClick={handleClear}
                      >
                        Clear
                      </button>
                    </div>
                  </form>
                </div>
              </div>

              {!hasSearched ? (
                <div className="box">
                  <div className="box-body">
                    <p style={{ marginBottom: 0 }}>
                      Enter a keyword (and optional exam/year), then click{" "}
                      <strong>Search</strong>. Matching questions appear first so
                      you can open them directly.
                    </p>
                  </div>
                </div>
              ) : loading ? (
                <Loader />
              ) : (
                <Fragment>
                  <div className="box box-success">
                    <div className="box-header with-border">
                      <h3 className="box-title">
                        1. Matching Questions ({questionHits.length})
                      </h3>
                    </div>
                    <div className="box-body table-responsive no-padding">
                      <table className="table table-hover">
                        <thead>
                          <tr>
                            <th>Question snippet</th>
                            <th style={{ width: "120px" }}>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {questionHits.length === 0 ? (
                            <tr>
                              <td colSpan="2">
                                No question text matched this keyword. Check
                                topics/sections below, or try a different phrase.
                              </td>
                            </tr>
                          ) : (
                            questionHits.map((hit) => (
                              <tr key={hit._id}>
                                <td>{hit.snippet || hit.title || "—"}</td>
                                <td>
                                  <button
                                    type="button"
                                    className="btn btn-primary btn-sm"
                                    onClick={() => openQuestionHit(hit)}
                                    disabled={!hit.subjectId}
                                  >
                                    Open questions
                                  </button>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="box">
                    <div className="box-header with-border">
                      <h3 className="box-title">
                        2. Matching Topics ({topicHits.length}) / Sections (
                        {sectionHits.length})
                      </h3>
                    </div>
                    <div className="box-body table-responsive no-padding">
                      <table className="table table-hover">
                        <thead>
                          <tr>
                            <th>Type</th>
                            <th>Name</th>
                            <th style={{ width: "140px" }}>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {topicHits.length === 0 && sectionHits.length === 0 ? (
                            <tr>
                              <td colSpan="3">
                                No topic or section names matched.
                              </td>
                            </tr>
                          ) : (
                            <Fragment>
                              {topicHits.map((hit) => (
                                <tr key={`topic-${hit._id}`}>
                                  <td>Topic</td>
                                  <td>{hit.title}</td>
                                  <td>
                                    <button
                                      type="button"
                                      className="btn btn-info btn-sm"
                                      onClick={() => openTopicHit(hit)}
                                      disabled={!hit.subjectId}
                                    >
                                      Open subject
                                    </button>
                                  </td>
                                </tr>
                              ))}
                              {sectionHits.map((hit) => (
                                <tr key={`section-${hit._id}`}>
                                  <td>Section</td>
                                  <td>{hit.title}</td>
                                  <td>
                                    <button
                                      type="button"
                                      className="btn btn-info btn-sm"
                                      onClick={() => openTopicHit(hit)}
                                      disabled={!hit.subjectId}
                                    >
                                      Open subject
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </Fragment>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="box">
                    <div className="box-header with-border">
                      <h3 className="box-title">
                        3. Related Subject / Year shortcuts ({rows.length})
                      </h3>
                    </div>
                    <div className="box-body table-responsive no-padding">
                      <p style={{ padding: "10px 15px 0", marginBottom: 0 }}>
                        Use this only as a shortcut into the bank location. For
                        keyword searches, years are limited to ones that contain
                        matching questions.
                      </p>
                      <table className="table table-hover">
                        <thead>
                          <tr>
                            <th>Exam</th>
                            <th>Subject</th>
                            <th>Year</th>
                            <th>Questions in year</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.length === 0 ? (
                            <tr>
                              <td colSpan="5" className="text-center">
                                No related subject/year shortcuts found.
                              </td>
                            </tr>
                          ) : (
                            rows.map((row) => (
                              <tr key={row.key}>
                                <td>{row.examName}</td>
                                <td>{row.subjectTitle}</td>
                                <td>{row.yearLabel}</td>
                                <td>{row.totalQuestions}</td>
                                <td>
                                  <button
                                    type="button"
                                    className="btn btn-info btn-sm"
                                    style={{ marginRight: "6px" }}
                                    onClick={() => openSubjectYears(row)}
                                  >
                                    <i className="fa fa-folder-open"></i> Years
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-success btn-sm"
                                    onClick={() => openYearTopics(row)}
                                    disabled={!row.yearId}
                                  >
                                    <i className="fa fa-list"></i> Topics
                                  </button>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                      <div style={{ padding: "10px" }}>
                        <button
                          className="btn btn-default btn-sm"
                          disabled={loading || page <= 1}
                          onClick={() => setPage((p) => Math.max(p - 1, 1))}
                        >
                          Prev
                        </button>
                        <span style={{ margin: "0 10px" }}>
                          Page {subjectPagination?.page || page} of{" "}
                          {subjectPagination?.totalPages || 1}
                        </span>
                        <button
                          className="btn btn-default btn-sm"
                          disabled={
                            loading ||
                            (subjectPagination?.page || 1) >=
                              (subjectPagination?.totalPages || 1)
                          }
                          onClick={() => setPage((p) => p + 1)}
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  </div>
                </Fragment>
              )}
            </div>
          </div>
        </section>
      </div>
    </Fragment>
  );
};

export default QuestionBankSearch;
